import { useState } from "react";
import { Check, X as XIcon, Save, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { hrs } from "@/lib/clipboard";
import { HAZARD_LABEL } from "@/lib/hazard";
import { forgetOutboxPw } from "@/components/OutboxPassword";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const xLen = (t) => t.replace(/https?:\/\/\S+/g, "x".repeat(23)).length;
const STATUS = {
  pending_approval: ["Needs approval", "text-amber-300 border-amber-400/40"],
  approved: ["Approved · waiting", "text-atlas-cyan border-atlas-cyan/40"],
  posted: ["Posted", "text-emerald-300 border-emerald-400/50"],
  failed: ["Failed", "text-atlas-red border-atlas-red/50"],
  rejected: ["Rejected", "text-atlas-secondary border-white/15"],
};
const CH = { pending: "pending", awaiting_keys: "awaiting keys", posted: "posted", failed: "failed", waiting_imagery: "waiting for clear imagery" };
const IMG = {
  clear: ["Cloud-free pair", "text-emerald-300 border-emerald-400/40"],
  waiting: ["Waiting for cloud-free pair", "text-amber-300 border-amber-400/40"],
  unavailable: ["No usable pair found", "text-atlas-red border-atlas-red/40"],
  no_later: ["Held · no later satellite pass yet", "text-amber-300 border-amber-400/40"],
};
const ImageryBadge = ({ it }) => {
  const [label, cls] = IMG[it.imagery] || IMG.waiting;
  const pct = it.pair_cloud == null ? "" : ` · ${Math.round(it.pair_cloud * 100)}% cloud`;
  return (
    <span className={`px-2 h-6 inline-flex items-center border text-[9px] font-mono uppercase tracking-wider ${cls}`} data-testid={`outbox-imagery-${it.uid}`}
      title="Posts only go out with a before/after pair under 10% cloud. Vigilo retries every 3 h for 14 days.">
      {label}{pct}
    </span>
  );
};
const BTN = "flex items-center gap-1.5 px-3 h-8 border text-[10px] font-mono uppercase tracking-wider transition-colors disabled:opacity-40";

const NAME = { x: "X", linkedin: "LinkedIn" };
const reportChannels = (res, paused) => {
  Object.entries(res.channels || {}).forEach(([k, c]) => {
    const n = NAME[k] || k;
    if (c.status === "posted") toast.success(`Posted to ${n}`);
    else if (c.status === "failed") toast.error(`${n} post failed: ${c.error || "unknown error"}`);
    else if (c.status === "waiting_imagery") toast.warning(`${n}: approved, waiting for a cloud-free image`);
    else if (c.status === "awaiting_keys") toast.warning(paused
      ? `${n}: approved but NOT sent — posting is paused (SOCIAL_PAUSED="true" in backend .env)`
      : `${n}: approved but NOT sent — ${n} keys are missing in backend .env`);
  });
};

export const OutboxItem = ({ it, xLimit, onChange, paused, ensurePw }) => {
  const [x, setX] = useState(it.x_text);
  const [li, setLi] = useState(it.linkedin_text);
  const [busy, setBusy] = useState(null);
  const locked = ["posted", "rejected"].includes(it.status);
  const dirty = x !== it.x_text || li !== it.linkedin_text;
  const fail = (e) => { if (/password/i.test(e.message)) forgetOutboxPw(); toast.error(e.message); };
  const run = async (kind, fn, ok) => {
    if (!(await ensurePw())) return;
    setBusy(kind);
    try { onChange({ ...it, ...(await fn()) }); toast.success(ok); } catch (e) { fail(e); } finally { setBusy(null); }
  };
  const save = () => run("save", async () => {
    const res = await api.editOutbox(it.uid, { x_text: x, linkedin_text: li });
    setX(res.x_text); setLi(res.linkedin_text);
    return res;
  }, "Draft saved");
  const blocked = it.imagery !== "clear"
    ? ({ unavailable: "No usable before/after pair was found — this card can't be posted.",
      no_later: "Held: no satellite capture newer than the BEFORE frame yet. Vigilo checks every 3 h and releases it once a later pass exists." }[it.imagery]
      || "Waiting for a cloud-free before/after pair (under 10% cloud). Vigilo retries every 3 h.")
    : xLen(x) > xLimit ? `X text is over ${xLimit} characters — shorten it first.` : null;
  const approve = async () => {
    if (blocked) return toast.error(blocked);
    if (!(await ensurePw())) return;
    setBusy("approve");
    try {
      if (dirty) await api.editOutbox(it.uid, { x_text: x, linkedin_text: li });
      const res = await api.approveOutbox(it.uid);
      onChange({ ...it, ...res });
      reportChannels(res, paused);
    } catch (e) { fail(e); } finally { setBusy(null); }
  };
  const resend = async () => {
    if (!(await ensurePw())) return;
    setBusy("wa");
    try {
      const a = await api.notifyOutbox(it.uid);
      onChange({ ...it, approval_alert: a });
      a.status === "sent" ? toast.success("WhatsApp alert sent") : toast.message(a.detail || `WhatsApp: ${a.status}`);
    } catch (e) { fail(e); } finally { setBusy(null); }
  };
  const [label, cls] = STATUS[it.status] || [it.status, ""];
  return (
    <article className="border border-white/10 bg-atlas-surface/80 grid md:grid-cols-[320px_1fr] atlas-rise" data-testid={`outbox-item-${it.uid}`}>
      <img src={`${BACKEND}/api/confirmed/${it.broadcast_uid}/card?size=square&v=${it.image_version || ""}`} alt="Share card" className="w-full bg-atlas-void object-contain" />
      <div className="p-5 flex flex-col gap-4 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400">{HAZARD_LABEL[it.hazard] || it.hazard} · lead {hrs(it.lead_hours)}</div>
            <div className="text-white font-display truncate">{it.region}</div>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <span className={`px-2 h-6 inline-flex items-center border text-[9px] font-mono uppercase tracking-wider ${cls}`} data-testid={`outbox-status-${it.uid}`}>{label}</span>
            {!locked && <ImageryBadge it={it} />}
          </div>
        </div>
        <label className="flex flex-col gap-1">
          <span className="flex justify-between text-[9px] font-mono uppercase tracking-widest text-atlas-secondary/85">
            X post <span className={xLen(x) > xLimit ? "text-atlas-red" : ""} data-testid={`outbox-x-count-${it.uid}`}>{xLen(x)}/{xLimit}</span>
          </span>
          <textarea value={x} onChange={(e) => setX(e.target.value)} disabled={locked} rows={4} data-testid={`outbox-x-text-${it.uid}`}
            className="bg-atlas-void border border-white/10 p-2 text-[12px] font-mono text-white focus:border-atlas-cyan/60 outline-none resize-y" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[9px] font-mono uppercase tracking-widest text-atlas-secondary/85">LinkedIn post</span>
          <textarea value={li} onChange={(e) => setLi(e.target.value)} disabled={locked} rows={9} data-testid={`outbox-li-text-${it.uid}`}
            className="bg-atlas-void border border-white/10 p-2 text-[12px] font-mono text-white focus:border-atlas-cyan/60 outline-none resize-y" />
        </label>
        <div className="flex flex-wrap gap-3 text-[10px] font-mono uppercase tracking-wider text-atlas-secondary">
          {Object.entries(it.channels).map(([k, c]) => (
            <span key={k} data-testid={`outbox-ch-${k}-${it.uid}`} title={c.error || ""}>
              {k === "x" ? "X" : "LinkedIn"}: <span className={c.status === "posted" ? "text-emerald-300" : c.status === "failed" ? "text-atlas-red" : "text-white"}>{CH[c.status] || c.status}</span>
              {c.url && <a href={c.url} target="_blank" rel="noopener noreferrer" className="ml-1 inline-flex text-atlas-cyan"><ExternalLink className="w-3 h-3" /></a>}
            </span>
          ))}
          {it.approval_alert && (
            <span data-testid={`outbox-wa-${it.uid}`} title={it.approval_alert.detail || ""}>
              WhatsApp alert: <span className={it.approval_alert.status === "sent" ? "text-emerald-300" : "text-amber-300"}>
                {(it.approval_alert.status || "pending").replace("_", " ")}</span>
              {it.status === "pending_approval" && (
                <button onClick={resend} disabled={!!busy} data-testid={`outbox-wa-resend-${it.uid}`} className="ml-2 text-atlas-cyan hover:underline disabled:opacity-40">
                  resend</button>
              )}
            </span>
          )}
        </div>
        {!locked && (
          <div className="flex flex-wrap gap-2">
            <button onClick={approve} disabled={!!busy} data-testid={`outbox-approve-${it.uid}`}
              title={blocked || ""}
              className={`${BTN} border-emerald-400/50 text-emerald-300 ${blocked ? "opacity-50 cursor-not-allowed" : "hover:bg-emerald-400 hover:text-atlas-void"}`}>
              {busy === "approve" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              {it.status === "failed" ? "Retry" : it.status === "approved" ? "Send now" : "Approve & post"}
            </button>
            <button onClick={save} disabled={!!busy || !dirty} data-testid={`outbox-save-${it.uid}`}
              className={`${BTN} border-white/15 text-atlas-secondary hover:text-white hover:border-white/30`}>
              <Save className="w-3.5 h-3.5" /> Save draft
            </button>
            <button onClick={() => run("reject", () => api.rejectOutbox(it.uid), "Rejected")} disabled={!!busy} data-testid={`outbox-reject-${it.uid}`}
              className={`${BTN} border-atlas-red/40 text-atlas-red hover:bg-atlas-red hover:text-white`}>
              <XIcon className="w-3.5 h-3.5" /> Reject
            </button>
            {(blocked || paused) && (
              <p className="basis-full text-[10px] font-mono text-amber-300/90" data-testid={`outbox-hint-${it.uid}`}>
                {blocked || "Posting is paused: approving queues the post, it goes out when SOCIAL_PAUSED is set to \"false\"."}
              </p>
            )}
          </div>
        )}
      </div>
    </article>
  );
};
